import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../style/category.css';
import { useSelector } from 'react-redux';

const CategoryForm = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', description: '', image: null });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  const isEditing = !!category;

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        image: category.image || null
      });
      setImagePreview(category.image || null);
    }
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData(prev => ({ ...prev, image: file }));
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setFormData(prev => ({ ...prev, image: file }));
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.image && !isEditing) newErrors.image = 'Image is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const cleanedData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        image: formData.image
      };
      await onSave(cleanedData);
    } catch (error) {
      console.error('Error saving category:', error);
      // Show more specific error messages based on the error response
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || 'An error occurred while saving the category.';
        toast.error(errorMessage);
      } else {
        toast.error('An error occurred while saving the category. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="category-modal-overlay">
      <div className="category-modal-container no-scrollbar">
        <div className="category-modal-content">
          <div className="category-modal-header">
            <h2 className="category-modal-title">
              {isEditing ? 'Edit Category' : 'Add New Category'}
            </h2>
            <p className="category-modal-subtitle">
              {isEditing ? 'Update category information.' : 'Create a new product category.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="category-form">
            <div className="category-modal-body">
              <div className="category-form-group">
                <label htmlFor="name" className="category-form-label">
                  Category Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`${isPartsURL ? "category-form-input-parts" : isAccessoriesURL ? "category-form-input-acc" : "category-form-input"} ${errors.name ? 'error' : ''}`}
                  placeholder="Mobile"
                  required
                />
                {errors.name && (
                  <div className="category-form-error">{errors.name}</div>
                )}
              </div>

              <div className="category-form-group">
                <label htmlFor="image" className="category-form-label">
                  Category Image *
                </label>
                <div 
                  className={`${isPartsURL ? "category-image-upload-parts" : isAccessoriesURL ? "category-image-upload-acc" : "category-image-upload"} ${errors.image ? 'error' : ''} ${imagePreview ? 'has-image' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('image').click()}
                >
                  {imagePreview ? (
                    <div className="category-image-preview">
                      <img src={imagePreview} alt="Category preview" className="category-preview-img" />
                      <div className="category-change-overlay">
                        <button 
                          type="button" 
                          className={`${isPartsURL ? "category-change-btn-parts" : isAccessoriesURL ? "category-change-btn-acc" : "category-change-btn"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById('image').click();
                          }}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="category-upload-icon">
                        <Upload size={24} color={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
                      </div>
                      <p className="category-upload-text">
                        The image should be JPG, PNG, WEBP format
                      </p>
                    </>
                  )}
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="category-file-input"
                    style={{ display: 'none' }}
                  />
                </div>
                {errors.image && (
                  <div className="category-form-error">{errors.image}</div>
                )}
              </div>

              <div className="category-form-group">
                <label htmlFor="description" className="category-form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`${isPartsURL ? "category-form-textarea-parts" : isAccessoriesURL ? "category-form-textarea-acc" : "category-form-textarea"}`}
                  rows="3"
                  placeholder="Mobile phone and smartphone"
                />
              </div>
            </div>

            <div className="category-modal-footer">
              <button
                type="button"
                className="category-btn-cancel"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${isPartsURL ? "category-btn-primary-parts" : isAccessoriesURL ? "category-btn-primary-acc" : "category-btn-primary"}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="category-spinner" />
                    Saving...
                  </>
                ) : (
                  isEditing ? 'Update Category' : 'Add Category'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoryForm;
